import dayjs from 'dayjs';
import {z} from 'zod';

import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {
  GetHealthLogMeasurementsForExportQuery,
  ListHealthLogMetricsForExportQuery,
} from '@/services/graphqlHooks';
import {
  useGetHealthLogMeasurementsForExportQuery,
  useListHealthLogMetricsForExportQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import {
  formatHealthLogValuesForDisplay,
  getMetricLabel,
} from '@/shared/residents/utils/healthLogUtils';

import HealthLogOptionsFields from '../../components/export-options/HealthLogOptionsFields';
import type {ExportDefinition} from '../../types/exportTypes';
import {
  describeDateRange,
  formatExportDateTime,
  getDateRangeFileName,
  getResidentDateRangeDefaultOptions,
  residentDateRangeOptionsSchema,
} from '../exportOptions';
import {EXPORT_HEALTH_LOG_METRIC_PARAM} from '../exportParams';
import {fetchAllPages} from '../fetchAllPages';

const healthLogOptionsSchema = residentDateRangeOptionsSchema.and(
  z.object({
    metricId: z.string().min(1, i18n.t('Measurement type is required')),
  })
);

export type HealthLogOptions = z.infer<typeof healthLogOptionsSchema>;

type HealthLogMetricExportDefinition =
  ListHealthLogMetricsForExportQuery['listHealthLogMetrics'][number];

type HealthLogExportRecord =
  GetHealthLogMeasurementsForExportQuery['getHealthLogMeasurements']['items'][number] & {
    metricDefinition?: HealthLogMetricExportDefinition;
  };

const loadMeasurements = ({
  residentId,
  metricId,
  startDate,
  endDate,
}: HealthLogOptions) =>
  fetchAllPages(async nextToken => {
    const variables = {
      residentId,
      metricId,
      from: dayjs(startDate).startOf('day').toISOString(),
      to: dayjs(endDate).endOf('day').toISOString(),
      nextToken,
    };
    const {getHealthLogMeasurements} = await queryClient.fetchQuery({
      queryKey: useGetHealthLogMeasurementsForExportQuery.getKey(variables),
      queryFn: useGetHealthLogMeasurementsForExportQuery.fetcher(
        graphqlClient,
        variables
      ),
    });

    return getHealthLogMeasurements;
  });

export const healthLogExport: ExportDefinition<
  HealthLogOptions,
  HealthLogExportRecord
> = {
  id: 'healthLog',
  label: i18n.t('Resident health log'),
  permission: 'EXPORT_HEALTH_LOG',
  entitlement: 'HEALTH_LOG',
  optionsSchema: healthLogOptionsSchema,
  OptionsFields: HealthLogOptionsFields,
  getDefaultOptions: searchParams => ({
    ...getResidentDateRangeDefaultOptions(searchParams),
    metricId: searchParams.get(EXPORT_HEALTH_LOG_METRIC_PARAM) ?? '',
  }),
  load: async options => {
    const {listHealthLogMetrics} = await queryClient.fetchQuery({
      queryKey: useListHealthLogMetricsForExportQuery.getKey(),
      queryFn: useListHealthLogMetricsForExportQuery.fetcher(graphqlClient),
    });
    const metricDefinition = listHealthLogMetrics.find(
      ({id}) => id === options.metricId
    );
    const measurements = await loadMeasurements(options);

    return measurements
      .map(measurement => ({...measurement, metricDefinition}))
      .sort((measurementA, measurementB) =>
        dayjs(measurementA.recordedAt).diff(measurementB.recordedAt)
      );
  },
  columns: [
    {
      header: i18n.t('Date'),
      value: measurement => formatExportDateTime(measurement.recordedAt),
    },
    {
      header: i18n.t('Value'),
      value: measurement =>
        formatHealthLogValuesForDisplay(
          measurement,
          measurement.metricDefinition
        )
          .map(
            ({name, value, unit}) =>
              `${name}: ${value}${unit ? ` ${unit}` : ''}`
          )
          .join(', '),
    },
  ],
  describeOptions: options =>
    `${getMetricLabel(options.metricId)}, ${describeDateRange(options)}`,
  getFileName: options =>
    getDateRangeFileName(`health-log-${options.metricId}`, options),
};
